import { useEffect, useMemo, useState } from "react";
import { Download, Sliders } from "lucide-react";
import { RANGE_MAX_ISO, addDaysIso, clampDateIso, clampRangeIso, daysAgoIso, formatDateRange, monthEndIso, trailingBucketsStartIso } from "@/lib/utils/daylight-insight/dates";
import { exportGridXlsx } from "@/lib/utils/daylight-insight/export";
import { MON } from "@/lib/utils/daylight-insight/constants";
import { fmtVal } from "@/lib/utils/daylight-insight/format";
import { pillarKey } from "@/lib/utils/daylight-insight/pillars";
import type { DateRange, Metric, MetricKind } from "@/lib/utils/daylight-insight/types";
import { useStore } from "@/provider/store";
import { DateRangePicker, DateSinglePicker, MultiSelect } from "@/components/daylight-insight/controls";
import { BranchBreakdownApi, type ApiBranchRow } from "@/components/daylight-insight/tables";
import { useBranchDetails } from "@/components/daylight-insight/api/useBranchDetails";
import { useAllMetricInterval } from "@/components/daylight-insight/api/useAllMetricInterval";
import BranchTablePanelSkeleton from "@/components/dashboard-insight/BranchTablePanelSkeleton";
import { MetricTrendsPanel, type RelationBase } from "@/components/dashboard-insight/MetricTrendsPanel";
import AllMetricsPeriodTable from "./AllMetricsPeriodTable";
import MetricDrillDownModal from "./MetricDrillDownModal";
import type {
  AllMetricIntervalParams,
  AllMetricIntervalRow,
  BranchDetailRow,
  BranchDetailsParams,
  DashboardMetric,
  DashboardMetricCategory,
  MetricDrillDownParams,
} from "@/components/daylight-insight/api/types";

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Pull the `MetricBranch` rows out of the branch-details SP document. */
function toBranchRows(data: unknown): BranchDetailRow[] {
  if (Array.isArray(data)) return data as BranchDetailRow[];
  if (data && typeof data === "object") {
    const mb = (data as { MetricBranch?: unknown }).MetricBranch;
    if (Array.isArray(mb)) return mb as BranchDetailRow[];
    for (const v of Object.values(data as Record<string, unknown>)) {
      if (Array.isArray(v)) return v as BranchDetailRow[];
    }
  }
  return [];
}

/** Pull the `MetricList` rows out of the interval SP document. */
function toMetricList(data: unknown): AllMetricIntervalRow[] {
  if (Array.isArray(data)) return data as AllMetricIntervalRow[];
  if (data && typeof data === "object") {
    const ml = (data as { MetricList?: unknown }).MetricList;
    if (Array.isArray(ml)) return ml as AllMetricIntervalRow[];
    for (const v of Object.values(data as Record<string, unknown>)) {
      if (Array.isArray(v)) return v as AllMetricIntervalRow[];
    }
  }
  return [];
}

/** Map the SP's `MetricCalc` to a formatting kind. */
function calcToKind(calc: string | undefined): MetricKind {
  const c = (calc || "").toLowerCase();
  if (c.includes("percent") || c === "pct") return "pct";
  if (c.includes("currency") || c.includes("money") || c.includes("dollar")) return "currency";
  if (c.includes("day")) return "days";
  if (c.includes("hour")) return "hours";
  if (c.includes("min")) return "minutes";
  return "count";
}

/** Interval-start ISO → column label ("Jun '25" for months, "Jun 1 '25"
 *  otherwise). Every label carries its year: a trailing window can cross a year
 *  boundary, and a bare month reads as the current one. */
function intervalLabel(iso: string, interval: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return iso;
  const mon = MON[Number(m[2]) - 1];
  const yy = m[1].slice(2);
  return interval.toUpperCase() === "MONTH" ? `${mon} '${yy}` : `${mon} ${Number(m[3])} '${yy}`;
}

/** Interval-start ISO → an UNAMBIGUOUS column label for the export ("Jun '25"
 *  for months, "Jun 1, 2025" otherwise). The on-screen header omits the year to
 *  save width; a spreadsheet is read out of context and can span a year end. */
function exportIntervalLabel(iso: string, interval: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return iso;
  const mon = MON[Number(m[2]) - 1];
  return interval.toUpperCase() === "MONTH"
    ? `${mon} '${m[1].slice(2)}`
    : `${mon} ${Number(m[3])}, ${m[1]}`;
}

/** A default metric column when the API row has no formatting metadata. */
const PLAIN_METRIC: Metric = { id: "", name: "", kind: "count", agg: "avg", base: 0, benchmark: null, formula: "", daily: [] };

interface IntervalMetric { metric: Metric; values: (number | null)[]; hasDrillDown: boolean; }
interface IntervalGroup { category: string; metrics: IntervalMetric[]; }

/** Buckets the Details drill asks for, counted inclusive of the "as of" bucket —
 *  twelve of them, at the grain of the pivot the drill was opened from. */
const DETAILS_BUCKET_SPAN = 12;

/** The SP sends `HasDrillDown` as a bit — 1 / "1" / true all mean "has records". */
const isDrillable = (v: unknown): boolean =>
  v === true || Number(v) === 1;

export function AllMetricsInsight() {
  const s = useStore();
  const { reg, sections, snap } = s;
  const [pivot, setPivot] = useState("branch");
  // By Branch: the last 7 days, ending on the current date.
  const [amRange, setAmRange] = useState<DateRange>({ start: daysAgoIso(7), end: RANGE_MAX_ISO });
  const [anchor, setAnchor] = useState(RANGE_MAX_ISO);                                     // By Month/Week/Day: an "as of" date, defaults to the current date
  // …except neither may run past the warehouse refresh date, which the config
  // only supplies after both defaults are already in state. Pulling them back
  // when it lands keeps the two pickers showing a day they'd actually accept —
  // and re-running is a no-op once they sit inside the window.
  const maxDateIso = s.maxDateIso;
  useEffect(() => {
    setAmRange((r) => clampRangeIso(r, maxDateIso));
    setAnchor((a) => clampDateIso(a, maxDateIso));
  }, [maxDateIso]);
  const [pQuery, setPQuery] = useState("");
  const [pillarFilter, setPillarFilter] = useState("all");                                // active MetricCategory; "all" = every pillar
  const [metricSel, setMetricSel] = useState<string[]>([]);                               // selected metric values (codes); empty = all
  const allIds = useMemo(() => [...sections.flatMap((x) => x.ids), ...snap.ids], [sections, snap]);
  const metrics = allIds.map((id) => reg[id]);

  // Switching pivot / pillar resets the metric selection (option sets differ).
  const changePivot = (p: string) => { setPivot(p); setMetricSel([]); setPillarFilter("all"); };
  const changePillar = (cat: string) => { setPillarFilter(cat); setMetricSel([]); };

  // ─── By Branch: live branch-details API (filters & DashboardID = null; only
  //     the date range + service line vary). Re-fires whenever those change. ────
  const branchParams: BranchDetailsParams | null = pivot === "branch" && s.serviceLineSystemID
    ? {
        dashboardID: null,
        serviceLineSystemID: s.serviceLineSystemID,
        startDate: amRange.start,
        endDate: amRange.end,
        filters: null,
      }
    : null;
  const branchDetails = useBranchDetails(branchParams);
  // In flight, or the service line hasn't resolved yet (params still null) —
  // either way there's nothing trustworthy to show, so shimmer instead.
  const branchLoading =
    pivot === "branch" && (branchDetails.loading || branchParams == null);
  const branchRows = useMemo(() => toBranchRows(branchDetails.data), [branchDetails.data]);
  const seededByName = useMemo(() => {
    const m = new Map<string, Metric>();
    for (const met of metrics) if (met) m.set(met.name.trim().toLowerCase(), met);
    return m;
  }, [metrics]);
  const branchApiMetrics = useMemo(() => {
    const seen = new Set<string>(); const out: Metric[] = [];
    for (const r of branchRows) {
      const code = String(r.MetricCode ?? "").trim();
      if (!code || seen.has(code.toLowerCase())) continue;
      seen.add(code.toLowerCase());
      const name = String(r.MetricShortDesc ?? code).trim();
      out.push({ ...(seededByName.get(name.toLowerCase()) ?? PLAIN_METRIC), id: code, name });
    }
    return out;
  }, [branchRows, seededByName]);
  const branchValueIndex = useMemo(() => {
    const idx = new Map<string, Map<string, number | null>>();
    for (const r of branchRows) {
      const code = String(r.MetricCode ?? "").trim().toLowerCase();
      const bid = String(r.BranchSystemID ?? "");
      if (!code || !bid) continue;
      if (!idx.has(code)) idx.set(code, new Map());
      idx.get(code)!.set(bid, num(r.MetricValue));
    }
    return idx;
  }, [branchRows]);
  const branchApiRows = useMemo(() => {
    const seen = new Set<string>(); const out: ApiBranchRow[] = [];
    for (const r of branchRows) {
      const bid = String(r.BranchSystemID ?? "");
      if (!bid || seen.has(bid)) continue;
      seen.add(bid);
      out.push({ id: bid, name: String(r.branch_name || r.branch_code || bid).trim() });
    }
    return out;
  }, [branchRows]);
  const branchValueOf = (rowId: string, metricId: string): number | null =>
    branchValueIndex.get(metricId.toLowerCase())?.get(rowId) ?? null;
  const branchCols = metricSel.length ? branchApiMetrics.filter((m) => metricSel.includes(m.id)) : branchApiMetrics;

  // ─── By Day/Week/Month: live interval API (DashboardID = null → all pillars;
  //     IntervalEndDate = the selected date; 12 intervals). ──────────────────────
  const intervalParams: AllMetricIntervalParams | null = pivot !== "branch" && s.serviceLineSystemID
    ? {
        intervalEndDate: anchor,
        interval: pivot.toUpperCase(),   // DAY | WEEK | MONTH
        serviceLineSystemID: s.serviceLineSystemID,
        branchSystemIDs: null,
        payorTypeSystemIDs: null,
        disciplines: null,
        repRosterSystemIDs: null,
        referralSubTypes: null,
        referralSourceNames: null,
        jobTitles: null,
        dashboardID: null,
        numberOfIntervals: 12,
      }
    : null;
  const intervalHook = useAllMetricInterval(intervalParams);
  const intervalRows = useMemo(() => toMetricList(intervalHook.data), [intervalHook.data]);
  // Reshape MetricList → period columns + per-category metric rows.
  const intervalGrid = useMemo(() => {
    const rows = intervalRows;
    const isoLabel = new Map<string, string>();
    for (const r of rows) {
      const iso = String(r.IntervalStartDate ?? "");
      if (iso) isoLabel.set(iso, intervalLabel(iso, String(r.Interval ?? "")));
    }
    // Two orderings, deliberately: `isoAsc` is chronological and stays the basis
    // for anything that reasons about time (current vs prior, the records
    // window), while `isoPeriods` is the DISPLAY order — newest period first, so
    // the grid reads latest → oldest left to right.
    const isoAsc = [...isoLabel.keys()].sort();
    const isoPeriods = [...isoAsc].reverse();
    const byCat = new Map<string, Map<string, { name: string; calc: string; lowerBetter: boolean; hasDrillDown: boolean; vals: Map<string, number | null> }>>();
    const catOrder: string[] = [];
    for (const r of rows) {
      const cat = String(r.MetricCategory ?? "Uncategorized").trim();
      const code = String(r.MetricCode ?? "").trim();
      if (!code) continue;
      if (!byCat.has(cat)) { byCat.set(cat, new Map()); catOrder.push(cat); }
      const cm = byCat.get(cat)!;
      if (!cm.has(code)) {
        cm.set(code, {
          name: String(r.MetricShortDesc ?? code).trim(),
          calc: String(r.MetricCalc ?? ""),
          lowerBetter: r.TargetHigherIsBetter === false || r.TargetHigherIsBetter === 0,
          hasDrillDown: false,
          vals: new Map(),
        });
      }
      // The flag rides on every (metric × interval) row; one drillable row is
      // enough to mark the metric, so the first-seen record can't lose it.
      if (isDrillable(r.HasDrillDown)) cm.get(code)!.hasDrillDown = true;
      cm.get(code)!.vals.set(String(r.IntervalStartDate ?? ""), num(r.Curr_MetricValue));
    }
    const groups: IntervalGroup[] = catOrder.map((cat) => ({
      category: cat,
      metrics: [...byCat.get(cat)!.entries()].map(([code, m]) => ({
        metric: { id: code, name: m.name, kind: calcToKind(m.calc), agg: "avg", base: 0, benchmark: null, formula: "", lowerBetter: m.lowerBetter, daily: [] } as Metric,
        values: isoPeriods.map((iso) => m.vals.get(iso) ?? null),
        hasDrillDown: m.hasDrillDown,
      })),
    }));

    // A DashboardMetric per grid metric, so the Details drill (MetricTrendsPanel,
    // the Operating View's own view) can be opened from here: it needs the metric
    // METADATA to render and to resolve related-metric names — the trend/summary
    // series it fetches itself from the relations API, keyed on the code + range.
    const drillMetrics: DashboardMetric[] = catOrder.flatMap((cat) =>
      [...byCat.get(cat)!.entries()].map(([code, m]) => {
        // Chronological: `currentValue`/`priorValue` below read off the END of
        // this series, so it must not follow the reversed display order.
        const series = isoAsc.map((iso) => m.vals.get(iso) ?? null).filter((v): v is number => v != null);
        return {
          code,
          name: m.name,
          category: cat,
          serviceLineSystemID: "",
          formula: "",
          benchmarkLabel: null,
          calc: m.calc,
          currentValue: series.length ? series[series.length - 1] : null,
          priorValue: series.length > 1 ? series[series.length - 2] : null,
          higherIsBetter: !m.lowerBetter,
          isKeyMetric: false,
          isCurrentState: false,
          sequence: 0,
          trend: [],
          branches: [],
        };
      }),
    );
    // `isoPeriods` rides along so the export can label columns with their year —
    // the on-screen header drops it for width.
    return { isoPeriods, isoAsc, periods: isoPeriods.map((iso) => isoLabel.get(iso)!), groups, drillMetrics };
  }, [intervalRows]);

  // The loaded data still belongs to the PREVIOUS interval right after a pivot
  // switch — treat that (and the not-yet-fetched state) as loading so the table
  // never flashes stale data. An empty (non-null) response is NOT loading.
  const dataInterval = String(intervalRows[0]?.Interval ?? "").toUpperCase();
  const intervalStale = pivot !== "branch" && dataInterval !== "" && dataInterval !== pivot.toUpperCase();
  const intervalLoading =
    pivot !== "branch" &&
    (intervalHook.loading || intervalStale || (intervalParams != null && intervalHook.data == null && !intervalHook.error));

  const q = pQuery.trim().toLowerCase();
  // Table groups after the active pillar + search + Metrics-dropdown filters.
  const visibleGroups = intervalGrid.groups
    .filter((g) => pillarFilter === "all" || g.category === pillarFilter)
    .map((g) => ({
      category: g.category,
      metrics: g.metrics.filter((im) =>
        (!q || im.metric.name.toLowerCase().includes(q)) &&
        (!metricSel.length || metricSel.includes(im.metric.id)),
      ),
    }))
    .filter((g) => g.metrics.length);

  const periods = intervalGrid.periods;
  const grainWord = pivot === "month" ? "months" : pivot === "week" ? "weeks" : "days";
  // Newest label is now FIRST, so that's the period the window ends on.
  const prefix = pivot !== "branch" && periods.length ? `Trailing ${periods.length} ${grainWord} · ending ${periods[0] || ""}` : "";

  // ─── Drill-downs (By Month / Week / Day) ────────────────────────────────────
  // Two different windows, because the two drills answer different questions:
  //
  //  • Records span exactly ONE column — the period whose value was clicked — so
  //    the record list reconciles with the single number the user clicked rather
  //    than with the whole trailing window.
  //  • Details (metric-relations / the summary SPs) spans TWELVE BUCKETS AT THE
  //    PIVOT'S OWN GRAIN: the "as of" date is the end date, and the start is
  //    twelve months / weeks / days back from it. The drill has to answer the
  //    same question the grid behind it does — a Week view's trend that silently
  //    covered a year read as the Month view's.
  //
  //    Computed from the anchor rather than read off `isoPeriods`, which
  //    shortened the window whenever the oldest period had no data. The SPs pick
  //    their bucketing from the range they're given, so the grain follows from
  //    the range alone; nothing else has to be sent.
  //
  // The branch/payor arguments deliberately mirror the interval call (null).
  const detailsStart = trailingBucketsStartIso(
    anchor,
    pivot === "week" ? "week" : pivot === "day" ? "day" : "month",
    DETAILS_BUCKET_SPAN,
  );
  const drillFilters = { branchSystemIDs: null, payorTypeSystemIDs: null };

  // Each interval's END date — the earliest of three bounds, so no clicked column
  // can ever fetch records the number on screen didn't count:
  //
  //  1. the day before the NEXT interval starts — the real bucket boundary,
  //     taken from the starts the SP returned rather than from calendar maths;
  //  2. this bucket's own natural end — the backstop for a gap in the grid. An
  //     interval with no rows for ANY metric gets no column, and without this the
  //     bucket before the gap would stretch across it;
  //  3. the anchor — the newest bucket is truncated there (the interval call is
  //     made with the same `intervalEndDate`), so a mid-month "as of" doesn't pull
  //     records past the value shown.
  //
  // ISO dates compare lexicographically, so `min` is a plain string comparison.
  // `isoAsc`, not `isoPeriods` — this reasons about time, and the display array
  // is newest-first.
  const periodEndIso = useMemo(() => {
    const naturalEnd = (startIso: string) =>
      pivot === "day" ? startIso
      : pivot === "week" ? addDaysIso(startIso, 6)
      : monthEndIso(startIso);
    const ends = new Map<string, string>();
    const asc = intervalGrid.isoAsc;
    asc.forEach((iso, i) => {
      const next = asc[i + 1];
      const bounds = [naturalEnd(iso), anchor];
      if (next) bounds.push(addDaysIso(next, -1));
      ends.set(iso, bounds.reduce((a, b) => (b < a ? b : a)));
    });
    return ends;
  }, [intervalGrid, anchor, pivot]);

  /** Open state: the metric being drilled, and the ONE period it was opened on. */
  const [recordsMetric, setRecordsMetric] = useState<
    { code: string; name: string; startDate: string; endDate: string } | null
  >(null);
  const [detailsStack, setDetailsStack] = useState<string[]>([]);
  const [detailsTab, setDetailsTab] = useState("chart");
  const [detailsPillar, setDetailsPillar] = useState("operations");

  const openRecords = (im: IntervalMetric, _category: string, periodIso: string) =>
    setRecordsMetric({
      code: im.metric.id,
      name: im.metric.name,
      startDate: periodIso,
      endDate: periodEndIso.get(periodIso) ?? anchor,
    });
  const openDetails = (im: IntervalMetric, category: string) => {
    // Route the drill's second-level nav off the metric's own pillar, exactly as
    // the Operating View does for the dashboard it sits on.
    setDetailsPillar(pillarKey(category));
    setDetailsStack([im.metric.id]);
    setDetailsTab("chart");
  };

  const drillDownParams: MetricDrillDownParams | null =
    recordsMetric && s.serviceLineSystemID
      ? {
          metricCode: recordsMetric.code,
          startDate: recordsMetric.startDate,
          endDate: recordsMetric.endDate,
          serviceLineSystemID: s.serviceLineSystemID,
          ...drillFilters,
        }
      : null;

  // The Details drill reads its trend/summary series from the relations + summary
  // APIs, so it needs the same date/filter context the grid was built with.
  const detailsRelationBase: RelationBase = {
    serviceLineSystemID: s.serviceLineSystemID,
    startDate: detailsStart,
    endDate: anchor,
    filters: null,
  };
  const detailsCategory: DashboardMetricCategory = {
    category: "All Metrics",
    metrics: intervalGrid.drillMetrics,
  };

  // Export the ACTIVE tab only, exactly as it reads on screen: By Branch writes
  // branches × metric columns, the period tabs write metrics × interval columns
  // grouped by pillar. Both respect the filters already applied to the view.
  const canExport = pivot === "branch" ? branchApiRows.length > 0 : visibleGroups.length > 0;
  const handleExport = () => {
    if (!canExport) return;
    if (pivot === "branch") {
      exportGridXlsx(
        "All Metrics · By Branch",
        ["Branch", ...branchCols.map((m) => m.name)],
        branchApiRows.map((b) => [
          b.name,
          ...branchCols.map((m) => {
            const v = branchValueOf(b.id, m.id);
            return v === null ? "—" : fmtVal(m, v);
          }),
        ]),
        formatDateRange(amRange),
      );
      return;
    }
    // One row per metric, preceded by a single-cell row naming its pillar — the
    // same grouping the grid shows.
    const rows: (string | number)[][] = [];
    visibleGroups.forEach((g) => {
      rows.push([g.category]);
      g.metrics.forEach((im) =>
        rows.push([
          im.metric.name,
          ...im.values.map((v) => (v === null ? "—" : fmtVal(im.metric, v))),
        ]),
      );
    });
    exportGridXlsx(
      `All Metrics · By ${pivot.charAt(0).toUpperCase()}${pivot.slice(1)}`,
      ["Metric", ...intervalGrid.isoPeriods.map((iso) => exportIntervalLabel(iso, pivot))],
      rows,
      prefix,
    );
  };

  // Metric dropdown = { value: MetricCode, label: MetricShortDesc }: from the API
  // response, scoped to the active pillar for the period views. Selection tracked
  // by value; the MultiSelect renders/accepts labels, so map both ways.
  const metricOpts = pivot === "branch"
    ? branchApiMetrics.map((m) => ({ value: m.id, label: m.name }))
    : intervalGrid.groups
        .filter((g) => pillarFilter === "all" || g.category === pillarFilter)
        .flatMap((g) => g.metrics.map((im) => ({ value: im.metric.id, label: im.metric.name })));
  const valueToLabel = new Map(metricOpts.map((o) => [o.value, o.label]));
  const labelToValue = new Map(metricOpts.map((o) => [o.label, o.value]));
  const metricOptions = Array.from(new Set(metricOpts.map((o) => o.label)));
  const selectedLabels = metricSel.map((v) => valueToLabel.get(v)).filter((l): l is string => !!l);

  return (
    <div className="dl-main">
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-.02em", margin: 0 }}>All Metrics</h2>
        <div className="dl-seg">
          {([["branch", "By Branch"], ["month", "By Month"], ["week", "By Week"], ["day", "By Day"]] as const).map(([k, lab]) => (
            <button key={k} className={pivot === k ? "on" : ""} onClick={() => changePivot(k)}>{lab}</button>
          ))}
        </div>
        <MultiSelect
          label="Metrics"
          icon={<Sliders size={14} style={{ color: "var(--ink-3)" }} />}
          options={metricOptions}
          selected={selectedLabels}
          onChange={(labels) => setMetricSel(labels.map((l) => labelToValue.get(l)).filter((v): v is string => !!v))}
          allLabel="All metrics"
        />
        <div style={{ flex: 1 }} />
        <button
          className="dl-btn"
          onClick={handleExport}
          disabled={!canExport}
          title={canExport ? "Export this tab" : "Nothing to export yet"}
        >
          <Download size={14} />Export
        </button>
        {pivot === "branch"
          ? <DateRangePicker range={amRange} onChange={setAmRange} maxIso={maxDateIso} />
          : <DateSinglePicker value={anchor} onChange={setAnchor} maxIso={maxDateIso} />}
      </div>

      {pivot === "branch" ? (
        // Swap the whole table for the skeleton while the branch-details call is
        // in flight — the previous response is still in state, so rendering the
        // table would leave stale rows up with no sign anything is loading after
        // a date-range or service-line change. Mirrors BranchTablePanel.
        branchLoading ? (
          <BranchTablePanelSkeleton />
        ) : (
          <BranchBreakdownApi
            metrics={branchCols}
            rows={branchApiRows}
            valueOf={branchValueOf}
            loading={false}
            error={branchDetails.error}
            // All Metrics isn't tied to a dashboard, so its saved views live
            // under `@DashboardID = NULL` — the same scope the branch-details
            // call above already uses.
            dashboardID={null}
            customViewType="branch"
          />
        )
      ) : (
        <AllMetricsPeriodTable
          pivot={pivot as "month" | "week" | "day"}
          prefix={prefix}
          isoPeriods={intervalGrid.isoPeriods}
          periodLabels={intervalGrid.periods}
          groups={visibleGroups}
          allGroups={intervalGrid.groups}
          loading={intervalLoading}
          error={intervalHook.error}
          query={pQuery}
          setQuery={setPQuery}
          pillarFilter={pillarFilter}
          onPillarChange={changePillar}
          onOpenRecords={openRecords}
          onOpenDetails={openDetails}
        />
      )}
      {/* HasDrillDown = 1 → the records behind ONE period's value, dynamic columns
          from the drill-down SP. Shared by the By Month / Week / Day views.
          The period is part of the key: clicking a different column refetches and
          resets the modal's column filters instead of leaving them over a
          different period's records. */}
      {recordsMetric && (
        <MetricDrillDownModal
          key={`${recordsMetric.code}:${recordsMetric.startDate}`}
          scope={{
            metricCode: recordsMetric.code,
            metricName: recordsMetric.name,
            scopeChips: [
              // The clicked period itself, not the trailing window — "June 2026",
              // or "Aug 1 – Aug 5, 2026" when the newest bucket stops at the anchor.
              formatDateRange({ start: recordsMetric.startDate, end: recordsMetric.endDate }),
              s.service === "hh" ? "Home Health" : "Hospice",
            ],
          }}
          params={drillDownParams}
          onClose={() => setRecordsMetric(null)}
        />
      )}
      {/* HasDrillDown = 0 → the same trends drill the Operating View opens on a
          metric click, driven by the relations/summary APIs for this window. */}
      {detailsStack.length > 0 && (
        <MetricTrendsPanel
          category={detailsCategory}
          // The drill's tabs and "More ▾" breakdowns are the DASHBOARD's
          // filters, so a metric drilled from All Metrics offers exactly what it
          // offers on its Operating View tab. No `focus`: read-only here.
          dashboardKey={detailsPillar}
          stack={detailsStack}
          setStack={setDetailsStack}
          onClose={() => setDetailsStack([])}
          tab={detailsTab}
          setTab={setDetailsTab}
          relationBase={detailsRelationBase}
          // The trends window this drill actually asked for — twelve monthly
          // buckets ending at the "as of" date, not the grid's own range. No
          // branch/payor chips: the call deliberately sends neither.
          scopeChips={[
            { value: formatDateRange({ start: detailsStart, end: anchor }), on: true },
            { value: s.service === "hh" ? "Home Health" : "Hospice" },
          ]}
        />
      )}
    </div>
  );
}
